import { ArrowRightLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/shared/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/ui/AlertDialog";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import { normalizeSearchText } from "../movement-allocations/utils";
import type { LedgerAllocationOperationType } from "../../types/accounting.types";

type Props = {
  source: LedgerAllocationOperationType | null
  candidates: LedgerAllocationOperationType[]
  budgetType: string
  isBusy: boolean
  onClose: () => void
  onConfirm: (targetId: string) => void
};

/**
 * ADM-621 - fusion de catégories, extraite du drawer d'allocation pour servir la
 * page Budgets. Reprend le même déroulé : on filtre, on choisit une cible, et on
 * annonce combien d'affectations vont bouger avant de confirmer.
 */
const CategoryMergeDialog = ({
  source, candidates, budgetType, isBusy, onClose, onConfirm,
}: Props) => {
  const [filter, setFilter] = useState("");
  const [targetId, setTargetId] = useState<string | null>(null);

  useEffect(() => {
    setFilter("");
    setTargetId(null);
  }, [source]);

  const filtered = useMemo(() => {
    if (!source) return [];
    const others = candidates.filter((candidate) => candidate.id !== source.id);
    const query = normalizeSearchText(filter);
    if (!query) return others;
    return others.filter((candidate) => normalizeSearchText(candidate.name).includes(query));
  }, [source, candidates, filter]);

  const target = targetId ? filtered.find((candidate) => candidate.id === targetId) : null;
  const movedCount = source?.ledger_allocations_count ?? 0;

  return (
    <AlertDialog open={source !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Fusionner «
            {" "}
            {source?.name}
            {" »"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Choisissez la catégorie cible (budget
            {" "}
            {budgetType}
            ). Toutes les affectations seront transférées, puis cette catégorie sera supprimée.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Input
            autoFocus
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filtrer par nom de catégorie"
            className="h-8 text-xs"
          />
          <div className="max-h-60 overflow-y-auto rounded-md border">
            {filtered.length === 0
              ? <div className="p-3 text-center text-xs text-slate-500">Aucune catégorie disponible.</div>
              : filtered.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  className={cn(
                    "flex w-full items-center justify-between gap-2 border-b px-3 py-1.5 text-left text-xs last:border-b-0 hover:bg-slate-50",
                    targetId === candidate.id && "bg-violet-50 font-medium",
                  )}
                  onClick={() => setTargetId(candidate.id)}
                >
                  <span className="truncate">
                    {candidate.name}
                    {candidate.actif === false && <span className="ml-1 text-[10px] text-slate-400">(inactive)</span>}
                  </span>
                  {(candidate.ledger_allocations_count ?? 0) > 0 && (
                    <span className="shrink-0 text-[10px] text-slate-500">
                      {candidate.ledger_allocations_count}
                      {" affectation(s)"}
                    </span>
                  )}
                </button>
              ))}
          </div>
          {target && source && (
            <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-700">
              <strong>{movedCount}</strong>
              {" affectation(s) seront déplacées vers "}
              <strong>{`« ${target.name} »`}</strong>
              {", puis "}
              <strong>{`« ${source.name} »`}</strong>
              {" sera supprimée. Cette action est irréversible."}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose} disabled={isBusy}>Annuler</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button disabled={!targetId || isBusy} onClick={() => targetId && onConfirm(targetId)}>
              Fusionner
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CategoryMergeDialog;
